import { Dispatch, FC, SetStateAction, useState } from 'react'
import cn from 'classnames'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import classes from './index.module.css'
import { Empty, Modal, Select } from 'antd'
import { GeneralCourseInformation, RefetchGeneralDataVariantType } from '../..'
import {
  CloseOutlined,
  DragOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import Button from '@UI/New/Button'
import { ElClassFormDataType } from '../PageContent/PageContent'
import CreateGroupModal from '../../../Manage/tabs/GroupsPage/components/CreateGroupModal/CreateGroupModal'
import EditTeacherModal from '../EditTeacherModal/EditTeacherModal'
import { getInArr } from '@/helpers/getInArr'
import { DragLineList } from '@UI/DragLineList'
import TariffSelect from './TariffSelect'
import { isEqualArrays } from './utils'

interface CourseListProps {
  generalData: GeneralCourseInformation
  elClassFormData: ElClassFormDataType
  setElClassFormData: Dispatch<SetStateAction<ElClassFormDataType>>
  refetchGeneralData: (variant: RefetchGeneralDataVariantType) => void
}

const CourseList: FC<CourseListProps> = ({
  generalData,
  elClassFormData,
  setElClassFormData,
  refetchGeneralData,
}) => {
  const [stateModal, setStateModal] = useState<{
    open: boolean
    type?: 'createGroup' | 'editTeacher'
    group?: NonNullable<ElClassFormDataType['groups']>[number] | undefined
  }>({
    open: false,
    type: 'createGroup',
    group: undefined,
  })

  const handleAddGroup = () => {
    setElClassFormData({
      ...elClassFormData,
      groups: [
        ...(elClassFormData.groups || []),
        {
          key:
            (elClassFormData.groups?.length ?? 0) +
            1 +
            '_' +
            new Date().getTime(),
          id: null,
          direction: {
            id: null,
            tariffs: [],
          },
        },
      ],
    })
  }

  const handleSelectDirection = (
    key: string | null | undefined,
    directionId: number | null,
  ) => {
    setElClassFormData({
      ...elClassFormData,
      groups: elClassFormData.groups?.map((group) => {
        if (group?.key === key && group?.direction?.id !== directionId) {
          const direction = generalData.allDirections?.find(
            (dir) => dir.id === directionId,
          )

          group = {
            key: group?.key,
            id: null,
            direction: {
              id: directionId,
              tariffs: direction?.directionTariffs
                ?.filter((tariff) => getInArr(tariff.type, ['free', 'month']))
                ?.map((tariff) => ({
                  id: tariff.id,
                  name: tariff.name,
                })),
            },
          }
        }

        return group
      }),
    })
  }

  const handleSelectGroup = (key: any, groupId: number | null | undefined) => {
    /*
     * Для сортировки (перемещения) курсов используется group.id как уникальный идентификатор,
     * то при смене группы, нужно менять и идентификатор в orders_group
     */

    const oldOrderGroup: number[] = elClassFormData.orders_group
      ? JSON.parse(elClassFormData.orders_group)
      : []
    let newOrderGroup: number[] = [...oldOrderGroup]

    if (groupId) {
      const oldGroupId = elClassFormData.groups?.find((g) => key && g?.key === key)?.id

      newOrderGroup = oldOrderGroup.map((id) => oldGroupId && id === oldGroupId ? groupId : id)

      if (isEqualArrays(newOrderGroup, oldOrderGroup)) {
        newOrderGroup.push(groupId)
      }
    }

    setElClassFormData({
      ...elClassFormData,
      groups: elClassFormData.groups?.map((group) => {
        if (group?.key === key) {
          if (group) {
            group.id = groupId
          } else {
            group = { id: groupId }
          }
        }

        return group
      }),
      orders_group: JSON.stringify(newOrderGroup),
    })
  }

  const handleSelectTariff = (
    key: any,
    tariffId: number | null | undefined,
  ) => {
    setElClassFormData({
      ...elClassFormData,
      groups: elClassFormData.groups?.map((group) => {
        if (group?.key === key) {
          if (group) {
            group.tariff_id = tariffId
          }
        }

        return group
      }),
    })
  }

  if (!elClassFormData.groups || elClassFormData.groups.length === 0)
    return (
      <div className={classes.emptyList}>
        <div className={classes.emptyListImage}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={'Нет курсов'}
          />
        </div>
        <Button
          color="secondary"
          className={classes.borderRadiusLg}
          onClick={handleAddGroup}
          leftContent={
            <PlusOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          }
        >
          Добавить курс
        </Button>
      </div>
    )

  return (
    <>
      <div className={cn(classes.listWrapper, 'customScroll')}>
        <div className={cn(classes.list)}>
          <div className={classes.listHeader}>
            <p className={mclasses.label}>Курс</p>
            <p className={mclasses.label}>Группа</p>
            <p className={mclasses.label}>Педагог</p>
            <p className={mclasses.label}>Тариф</p>
          </div>
          <DragLineList
            listOrder={
              elClassFormData.orders_group
                ? JSON.parse(elClassFormData.orders_group)
                : []
            }
            updateListOrdering={(newListOrder) => {
              setElClassFormData({
                ...elClassFormData,
                orders_group: JSON.stringify(newListOrder),
              })
            }}
            items={elClassFormData.groups.map((group, i) => ({
              id: group?.id ?? i,
              children: ({ overlayItem, dragElementProps }) => (
                <div key={group?.id ?? i} className={classes.listItem}>
                  {/* Направления / курсы */}
                  <Select
                    showSearch
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={iclasses.newSelectPopup}
                    placeholder="Выберите курс"
                    value={group?.direction?.id}
                    onChange={(value) =>
                      handleSelectDirection(group?.key, value)
                    }
                    filterOption={(input, option) =>
                      (option?.label?.toLowerCase() ?? '').includes(
                        input.toLowerCase(),
                      )
                    }
                    options={generalData.allDirections
                      ?.filter(
                        ({ id }) =>
                          id === group?.direction?.id ||
                          !elClassFormData.groups?.find(
                            (gr) => gr?.direction?.id === id,
                          ),
                      )
                      ?.map(({ id: value, name: label }) => ({ label, value }))
                      ?.sort((a, b) => (a.label > b.label ? 1 : -1))}
                  />
                  {/* Группы */}
                  <Select<number | string | null | undefined>
                    showSearch
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={cn(
                      iclasses.newSelectPopup,
                      classes.selectWithCreateButton,
                    )}
                    placeholder="Выберите группу"
                    value={'' + (group?.id ?? '-')}
                    onChange={(value, option) => {
                      if (value === 'create_group') {
                        setStateModal({
                          open: true,
                          type: 'createGroup',
                          group: group,
                        })
                      } else {
                        handleSelectGroup(group?.key, Number(value))
                      }
                    }}
                    filterOption={(input, option) =>
                      String(option?.label ?? '')
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={
                      group?.direction?.id
                        ? [
                            {
                              label: 'Создать группу',
                              value: 'create_group',
                              'data-create-element': true,
                            },
                            ...generalData.allGroups
                              ?.filter(
                                (g) =>
                                  '' + g.direction_id ===
                                  '' + group.direction?.id,
                              )
                              ?.map(({ id: value, name: label }) => ({
                                label,
                                value,
                              }))
                              ?.sort((a, b) => (a.label > b.label ? 1 : -1)),
                          ]
                        : []
                    }
                    notFoundContent={
                      group?.direction?.id
                        ? 'Нет доступных групп'
                        : 'Сначала выберите направление'
                    }
                  />
                  {/* Педагог */}
                  <button
                    className={classes.buttonField}
                    onClick={() => {
                      setStateModal({
                        open: true,
                        type: 'editTeacher',
                        group: group,
                      })
                    }}
                  >
                    <p className={classes.buttonFieldText}>
                      {/* { group?.seminarian?.name ?? '-' } */}
                      {generalData.allGroups?.find(
                        (el) => el.id === String(group?.id),
                      )?.name &&
                      group?.id &&
                      group?.direction?.id ? (
                        <>
                          {
                            generalData.allGroups?.find(
                              (el) => el.id === String(group.id),
                            )?.teacher.name
                          }
                        </>
                      ) : (
                        '-'
                      )}
                    </p>
                    <span className={classes.buttonFieldIcon}>
                      <EditOutlined
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    </span>
                  </button>
                  {/* Тарифы */}
                  <TariffSelect
                    group={group}
                    handleSelectTariff={handleSelectTariff}
                    className={classes.selectField}
                  />

                  {/* Actions */}
                  <button
                    className={classes.buttonActions}
                    {...dragElementProps}
                  >
                    <DragOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </button>
                  <button
                    className={classes.buttonActions}
                    onClick={() =>
                      Modal.confirm({
                        title: 'Вы уверены что хотите удалить?',
                        content: `Группу "${
                          group?.name ?? '(без названия)'
                        }", курс "${
                          group?.direction?.name ?? '(без названия)'
                        }"`,
                        icon: null,
                        centered: true,
                        maskClosable: true,
                        className: jclasses.modal,
                        okText: 'Подтвердить',
                        cancelText: 'Отменить',
                        onOk: () => {
                          setElClassFormData({
                            ...elClassFormData,
                            groups: elClassFormData.groups?.filter(
                              (gr) => gr?.key !== group?.key,
                            ),
                          })
                        },
                      })
                    }
                  >
                    <CloseOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </button>
                </div>
              ),
            }))}
          />
        </div>
      </div>
      <Button
        color="secondary"
        className={classes.borderRadiusLg}
        onClick={handleAddGroup}
        leftContent={
          <PlusOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
      >
        Добавить курс
      </Button>

      {/* Modals */}
      <CreateGroupModal
        directionId={Number(stateModal.group?.direction?.id)}
        open={stateModal.open && stateModal.type === 'createGroup'}
        onClose={() => setStateModal({ open: false })}
        onSuccess={() => {
          refetchGeneralData('groups')
          setStateModal({ open: false })
        }}
      />
      <EditTeacherModal
        group={stateModal.group}
        open={stateModal.open && stateModal.type === 'editTeacher'}
        onClose={() => setStateModal({ open: false })}
        onSuccess={(teacher) => {
          refetchGeneralData('groups')
          // setElClassFormData(prevState => ({ ...prevState, groups: prevState.groups?.map(gr => gr?.id === stateModal.group?.id ? ({ ...gr, seminarian: teacher }) : gr) }))
          setStateModal({ open: false })
        }}
      />
    </>
  )
}

export default CourseList
